import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, resolveComponent as _resolveComponent, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, unref as _unref, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "d-flex align-center gap-1" }
const _hoisted_2 = {
  key: 0,
  class: "text-body-2 text-grey-darken-1 mb-4"
}
const _hoisted_3 = {
  key: 1,
  class: "flex items-center gap-3 mb-4",
  "data-cy": "deposit-date-range"
}
const _hoisted_4 = { style: {"width":"170px"} }
const _hoisted_5 = {
  key: 2,
  class: "text-caption text-grey mb-4"
}
const _hoisted_6 = {
  key: 0,
  class: "py-4 text-center"
}
const _hoisted_7 = { class: "font-weight-bold mb-1" }
const _hoisted_8 = { style: {"width":"40px"} }
const _hoisted_9 = { class: "d-inline-flex" }
const _hoisted_10 = { class: "text-body-2" }
const _hoisted_11 = { class: "text-body-2" }
const _hoisted_12 = { class: "text-body-2" }
const _hoisted_13 = { class: "text-body-2 text-right" }
const _hoisted_14 = { key: 0 }
const _hoisted_15 = {
  key: 0,
  class: "d-flex justify-space-between align-center px-3 py-3 mb-3",
  style: {"border-top":"2px solid #e0e0e0"}
}
const _hoisted_16 = {
  key: 1,
  class: "text-body-2 text-orange-darken-3 mb-3",
  "data-cy": "all-deposits-locked-hint"
}
const _hoisted_17 = {
  key: 2,
  class: "text-body-2 text-orange-darken-3 mb-3",
  "data-cy": "no-deposits-selected-hint"
}
const _hoisted_18 = {
  key: 3,
  class: "mb-4"
}
const _hoisted_19 = { class: "text-h6 font-weight-bold" }
const _hoisted_20 = {
  key: 4,
  class: "text-body-2 text-error mb-2"
}
const _hoisted_21 = {
  key: 5,
  class: "sett-step-footer"
}

import { computed, type PropType } from 'vue';
import LlCheckbox from '@/components/LlCheckbox.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlTooltip from '@/components/LlTooltip.vue';
import LlDatePicker from '@/components/LlDatePicker.vue';
import LlIcon from '@/components/LlIcon.vue';
import AlertBar from '@/components/AlertBar.vue';
import { formatCurrency, getShortDateString, yyyymmdddash, parseLocalDate } from '@/services/utils';
import type { DepositItem, DepositsState } from '@/models/settlementTypes';


export default /*@__PURE__*/_defineComponent({
  __name: 'SettlementDepositStep',
  props: {
  deposits: { type: Object as PropType<DepositsState>, required: true },
  allDepositsSelected: { type: Boolean, default: false },
  recoveryAmount: { type: Number, default: 0 },
  feePercentage: { type: Number, default: 0 },
  canConfirm: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  validationError: { type: String, default: '' },
  /** Pre-V4 only — drives the matter-level-billing notice. */
  isClientLevelBilling: { type: Boolean, default: false },
  /** `settlementClientAccounting` flipper: on, drops the matter-only copy and the notice. */
  clientAccountingEnabled: { type: Boolean, default: false },
  /** Gate all SC-45762 redesign UI behind this flag.
   *  false = old design (description paragraph, caption, tfoot, fee % section).
   *  true  = new design (tooltip header, total outside table, no contingency %). */
  redesignEnabled: { type: Boolean, default: false },
},
  emits: [
  'toggle-all-deposits',
  'toggle-deposit',
  'open-trust-deposit',
  'apply-date-range',
  'confirm-recovery',
],
  setup(__props, { emit: __emit }) {

const props = __props;

const emit = __emit;

// Retired by AC-V4-1 once the flipper is on — the backend resolves the client ledger, so
// there is nothing left to warn about.
const showClientLevelNotice = computed(
  () => !props.clientAccountingEnabled && props.isClientLevelBilling,
);

// Present while blocked so the step keeps its shape, and otherwise only once there is
// something to confirm — an empty or still-loading list has never offered the button.
const showConfirmFooter = computed(
  () =>
    showClientLevelNotice.value ||
    (!props.deposits.loading && props.deposits.items.length > 0),
);

// No figure while blocked — it would be summed from the list we are withholding.
const confirmLabel = computed(() =>
  showClientLevelNotice.value
    ? 'Confirm Recovery'
    : `Confirm Recovery (${formatCurrency(props.recoveryAmount)})`,
);

// Drop the branch, keeping the neutral string, when the flipper goes.
const emptyStateHeading = computed(() =>
  props.clientAccountingEnabled
    ? 'No trust deposits found.'
    : 'No deposits found in trust for this matter.',
);

// The store's active filter is the single source of truth — the picker reads
// from it one-way (no local ref, no sync watcher), so a store reload updates
// the picker automatically without risking a feedback loop.
const startDateValue = computed<Date | null>(() => parseLocalDate(props.deposits.filterStartDate));

// Changing the picker reloads the deposits. The equality guard swallows the
// echo LlDatePicker re-emits when its bound value changes after a store reload,
// preventing a reload loop.
function onStartDateChange(val: Date | null) {
  const next = yyyymmdddash(val) || undefined;
  if (next === (props.deposits.filterStartDate ?? undefined)) return;
  emit('apply-date-range', { startDate: next });
}

const activeRangeLabel = computed(() => {
  const start = props.deposits.filterStartDate;
  return `${start ? getShortDateString(start) : '—'} to today`;
});

const allDepositsLocked = computed(
  () => props.deposits.items.length > 0 && props.deposits.items.every((d) => d.isLocked),
);

/** DD-V4-7 hover. Undefined when the row is selectable — LlTooltip then renders the
 *  bare checkbox with no tooltip wrapper. */
function lockedHoverText(deposit: DepositItem): string | undefined {
  if (!deposit.isLocked) return undefined;
  // trim() not ??: a blank name is not null, and would render "for matter ."
  const matterName = deposit.lockedByMatterName?.trim() || 'another matter';
  return (
    `You cannot select this deposit because it is associated with a settlement for matter ${matterName}. `
    + 'If this is in error, please edit that settlement to exclude this deposit.'
  );
}

return (_ctx: any,_cache: any) => {
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_table = _resolveComponent("v-table")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card = _resolveComponent("v-card")!

  return (_openBlock(), _createBlock(_component_v_card, {
    flat: "",
    class: "border-0 !bg-[rgb(var(--v-theme-surface))]"
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_card_title, { class: "d-flex align-center justify-space-between py-3 px-4" }, {
        default: _withCtx(() => [
          _createElementVNode("span", _hoisted_1, [
            _cache[3] || (_cache[3] = _createTextVNode(" Step 1: Select Settlement Deposits ", -1)),
            _createVNode(LlTooltip, {
              icon: "info",
              text: "Select the trust deposits that make up the settlement recovery. The recovery amount is the sum of all selected deposits."
            })
          ]),
          _createVNode(LlSecondaryButton, {
            color: "primary",
            size: "small",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('open-trust-deposit')))
          }, {
            default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
              _createTextVNode(" + New Deposit ", -1)
            ]))]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_component_v_card_text, null, {
        default: _withCtx(() => [
          (!__props.redesignEnabled)
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, " Select the trust deposits that make up the settlement recovery. The recovery amount is the sum of all selected deposits. "))
            : _createCommentVNode("", true),
          (!showClientLevelNotice.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createElementVNode("div", _hoisted_4, [
                  _createVNode(LlDatePicker, {
                    "model-value": startDateValue.value,
                    label: "Deposits as of",
                    "data-cy": "deposit-start-date",
                    "show-button": false,
                    "hide-details": true,
                    "onUpdate:modelValue": onStartDateChange
                  }, null, 8, ["model-value"])
                ])
              ]))
            : _createCommentVNode("", true),
          (!__props.redesignEnabled)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _cache[5] || (_cache[5] = _createTextVNode(" Showing trust deposits from ", -1)),
                _createElementVNode("strong", null, _toDisplayString(activeRangeLabel.value), 1),
                _cache[6] || (_cache[6] = _createTextVNode(". ", -1))
              ]))
            : _createCommentVNode("", true),
          (showClientLevelNotice.value)
            ? (_openBlock(), _createBlock(AlertBar, {
                key: 3,
                class: "mb-4",
                "data-cy": "client-level-billing-notice"
              }, {
                default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                  _createTextVNode(" Settlement deposits currently only support matter-level billing. This matter uses client-level billing, so some deposits may not appear here. ", -1)
                ]))]),
                _: 1
              }))
            : _createCommentVNode("", true),
          (!showClientLevelNotice.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 4 }, [
                (__props.deposits.loading)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                      _createVNode(_component_v_progress_circular, {
                        indeterminate: "",
                        color: "success",
                        size: "28"
                      })
                    ]))
                  : (__props.deposits.items.length === 0)
                    ? (_openBlock(), _createBlock(AlertBar, {
                        key: 1,
                        class: "mb-4"
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("div", _hoisted_7, _toDisplayString(emptyStateHeading.value), 1),
                          _cache[8] || (_cache[8] = _createElementVNode("div", { class: "text-body-2" }, "Widen the date range above, or use the + New Deposit button to record one.", -1))
                        ]),
                        _: 1
                      }))
                    : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                        _createVNode(_component_v_table, {
                          density: "compact",
                          class: "mb-2"
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("thead", null, [
                              _createElementVNode("tr", null, [
                                _createElementVNode("th", _hoisted_8, [
                                  _createVNode(LlCheckbox, {
                                    "model-value": __props.allDepositsSelected,
                                    disabled: allDepositsLocked.value,
                                    "aria-label": "Select all deposits",
                                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('toggle-all-deposits')))
                                  }, null, 8, ["model-value", "disabled"])
                                ]),
                                _cache[9] || (_cache[9] = _createElementVNode("th", { class: "text-uppercase text-caption" }, "Date", -1)),
                                _cache[10] || (_cache[10] = _createElementVNode("th", { class: "text-uppercase text-caption" }, "Payor / Source", -1)),
                                _cache[11] || (_cache[11] = _createElementVNode("th", { class: "text-uppercase text-caption" }, "Description", -1)),
                                _cache[12] || (_cache[12] = _createElementVNode("th", { class: "text-uppercase text-caption text-right" }, "Amount", -1))
                              ])
                            ]),
                            _createElementVNode("tbody", null, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.deposits.items, (dep) => {
                                return (_openBlock(), _createElementBlock("tr", {
                                  key: dep.depositId,
                                  class: _normalizeClass({
                  'bg-green-50': __props.deposits.selectedIds.includes(dep.depositId),
                  'text-disabled': dep.isLocked,
                })
                                }, [
                                  _createElementVNode("td", null, [
                                    _createVNode(LlTooltip, {
                                      location: "top",
                                      "max-width": 300,
                                      text: lockedHoverText(dep)
                                    }, {
                                      activator: _withCtx(() => [
                                        _createElementVNode("span", _hoisted_9, [
                                          _createVNode(LlCheckbox, {
                                            "model-value": __props.deposits.selectedIds.includes(dep.depositId),
                                            disabled: dep.isLocked,
                                            "aria-label": `Select deposit from ${dep.receivedFrom} for ${_unref(formatCurrency)(dep.amount)}`,
                                            class: "d-inline-flex",
                                            "onUpdate:modelValue": ($event: any) => (_ctx.$emit('toggle-deposit', dep.depositId))
                                          }, null, 8, ["model-value", "disabled", "aria-label", "onUpdate:modelValue"])
                                        ])
                                      ]),
                                      _: 2
                                    }, 1032, ["text"])
                                  ]),
                                  _createElementVNode("td", _hoisted_10, _toDisplayString(_unref(getShortDateString)(dep.receivedDate)), 1),
                                  _createElementVNode("td", _hoisted_11, _toDisplayString(dep.receivedFrom), 1),
                                  _createElementVNode("td", _hoisted_12, _toDisplayString(dep.description), 1),
                                  _createElementVNode("td", _hoisted_13, _toDisplayString(_unref(formatCurrency)(dep.amount)), 1)
                                ], 2))
                              }), 128))
                            ]),
                            (!__props.redesignEnabled)
                              ? (_openBlock(), _createElementBlock("tfoot", _hoisted_14, [
                                  _createElementVNode("tr", null, [
                                    _cache[13] || (_cache[13] = _createElementVNode("td", {
                                      colspan: "4",
                                      class: "text-body-1 font-weight-bold py-3"
                                    }, "Selected Recovery Amount:", -1)),
                                    _createElementVNode("td", {
                                      class: _normalizeClass(["text-body-1 font-weight-bold text-right py-3", __props.recoveryAmount > 0 ? 'text-green-darken-2' : ''])
                                    }, _toDisplayString(_unref(formatCurrency)(__props.recoveryAmount)), 3)
                                  ])
                                ]))
                              : _createCommentVNode("", true)
                          ]),
                          _: 1
                        }),
                        (__props.redesignEnabled)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                              _cache[14] || (_cache[14] = _createElementVNode("span", { class: "text-body-1 font-weight-bold" }, "Selected Recovery Amount:", -1)),
                              _createElementVNode("span", {
                                class: _normalizeClass(["font-weight-bold", __props.recoveryAmount > 0 ? 'text-green-darken-2' : 'text-grey-lighten-1']),
                                style: {"font-size":"16px"}
                              }, _toDisplayString(_unref(formatCurrency)(__props.recoveryAmount)), 3)
                            ]))
                          : _createCommentVNode("", true),
                        (allDepositsLocked.value)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_16, " Every deposit in this date range is already part of a settlement for another matter. Widen the date range above, edit that settlement to exclude a deposit, or record a new one. "))
                          : (__props.deposits.selectedIds.length === 0)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_17, " Select at least one deposit to define the recovery amount "))
                            : _createCommentVNode("", true),
                        (!__props.redesignEnabled)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                              _cache[15] || (_cache[15] = _createElementVNode("div", { class: "text-overline text-grey-darken-1 mb-1" }, "Contingency Fee %", -1)),
                              _createElementVNode("div", _hoisted_19, _toDisplayString(__props.feePercentage) + "%", 1),
                              _cache[16] || (_cache[16] = _createElementVNode("div", { class: "text-caption text-grey" }, "Configured in Settlement Settings above.", -1))
                            ]))
                          : _createCommentVNode("", true),
                        (__props.validationError)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                              _createVNode(LlIcon, {
                                icon: "mdi-alert-circle",
                                size: "14",
                                color: "error",
                                class: "mr-1"
                              }),
                              _createTextVNode(" " + _toDisplayString(__props.validationError), 1)
                            ]))
                          : _createCommentVNode("", true)
                      ], 64))
              ], 64))
            : _createCommentVNode("", true),
          (showConfirmFooter.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                _createVNode(LlSecondaryButton, {
                  color: "primary",
                  style: {"margin-left":"auto"},
                  disabled: !__props.canConfirm || showClientLevelNotice.value,
                  loading: __props.saving,
                  onClick: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('confirm-recovery')))
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(confirmLabel.value) + " → ", 1)
                  ]),
                  _: 1
                }, 8, ["disabled", "loading"])
              ]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})